import React, { useEffect, useState } from "react";
import { Box, Card, CardContent, Typography, Button, Divider, DialogActions, DialogContent, TextField, Dialog, DialogTitle, IconButton, FormControl, Select, MenuItem, Snackbar, Alert } from "@mui/material";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import { getData, postData, API_ENDPOINTS } from "../../services/apiService.js";
import LoadingSpinner from "../LoadingContext";
import "./Settings.css";

function Package() {
    const navigate = useNavigate();

    const [packages, setPackages] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const [packageType, setPackageType] = useState("");
    const [minWeight, setMinWeight] = useState("");
    const [maxWeight, setMaxWeight] = useState("");
    const [basePrice, setBasePrice] = useState("");
    const [perMileRate, setPerMileRate] = useState("");
    const [handlingFee, setHandlingFee] = useState("");
    const [status, setStatus] = useState("");
    const [description, setDescription] = useState("");

    const [editMode, setEditMode] = useState(false);
    const [selectedPackage, setSelectedPackage] = useState(null);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteId, setDeleteId] = useState(null);

    const handleCloseDialog = () => setOpenDialog(false);

    const resetForm = () => {
        setPackageType("");
        setMinWeight("");
        setMaxWeight("");
        setBasePrice("");
        setPerMileRate("");
        setHandlingFee("");
        setStatus("");
        setDescription("");
        setSelectedPackage(null);
        setEditMode(false);
    };

    const fetchPackages = async () => {
        try {
            setLoading(true);
            const response = await getData(API_ENDPOINTS.getPackages);
            if (response?.statusCode === 200) {
                setPackages(response.result.packageSizes || []);
            }
        } catch (error) {
            console.error("Error fetching packages:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPackages();
    }, []);

    const handleOpenDialog = (pkg) => {
        if (pkg) {
            setEditMode(true);
            setSelectedPackage(pkg);
            setPackageType(pkg.packageType);
            setMinWeight(pkg.minWeight);
            setMaxWeight(pkg.maxWeight);
            setBasePrice(Number(pkg.basePrice));
            setPerMileRate(Number(pkg.perMileRate));
            setHandlingFee(Number(pkg.handlingFee));
            setStatus(pkg.status);
            setDescription(pkg.description);
        } else {
            resetForm();
            setEditMode(false);
        }
        setOpenDialog(true);
    };

    const handleSave = async () => {
        setSaving(true);
        const payload = {
            id: selectedPackage?.id,
            packageType,
            minWeight: Number(minWeight),
            maxWeight: Number(maxWeight),
            basePrice: Number(basePrice),
            perMileRate: Number(perMileRate),
            handlingFee: Number(handlingFee),
            status: status.toLowerCase(),
            description,
        };

        try {
            let response;
            if (editMode) {
                response = await postData(API_ENDPOINTS.updatePackages, payload);
            } else {
                response = await postData(API_ENDPOINTS.createPackages, payload);
            }

            if (response?.statusCode === 200) {
                await fetchPackages();
                resetForm();
                handleCloseDialog();
                setSnackbar({
                    open: true,
                    message: response.message || "Package saved successfully!",
                    severity: "success",
                });
            } else {
                setSnackbar({
                    open: true,
                    message: response?.message || "Failed to save/update package!",
                    severity: "error",
                });
            }
        } catch (error) {
            setSnackbar({
                open: true,
                message: "Something went wrong while saving!",
                severity: "error",
            });
            console.error("API error:", error);
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteConfirm = async () => {
        if (!deleteId) return;
        try {
            const response = await postData(
                `${API_ENDPOINTS.deletePackages}?id=${deleteId}`,
                {}
            );
            if (response?.statusCode === 200) {
                await fetchPackages();
                setDeleteDialogOpen(false);
                setDeleteId(null);
                setSnackbar({
                    open: true,
                    message: response.message || "Package deleted successfully!",
                    severity: "success",
                });
            } else {
                setSnackbar({
                    open: true,
                    message: response?.message || "Failed to delete package!",
                    severity: "error",
                });
            }
        } catch (error) {
            setSnackbar({
                open: true,
                message: "Something went wrong while deleting!",
                severity: "error",
            });
            console.error("Delete API error:", error);
        }
    };

    return (
        <div className="package-container">
            <LoadingSpinner isLoading={saving || loading} />

            <div className="d-flex">
                <img
                    src="/smart-vectors/Back.svg"
                    alt="back"
                    style={{ width: 32, height: 32, cursor: "pointer", display: "block" }}
                    onClick={() => navigate(`/app/settings`)}
                />
                <h2 className="Package">Package Management System</h2>
            </div>

            {/* Summary Cards */}
            <Box display="flex" gap={2} flexWrap="wrap" mt={2}>
                <CardSummary title="Active Sizes" value={packages.length} />
                <CardSummary
                    title="Average Base Price"
                    value={
                        packages.length > 0
                            ? `₹${(packages.reduce((sum, p) => sum + Number(p.basePrice), 0) / packages.length).toFixed(2)}`
                            : "₹0"
                    }
                />
                <CardSummary
                    title="Maximum Weight"
                    value={packages.length > 0 ? `${Math.max(...packages.map((p) => p.maxWeight))} kg` : "0 kg"}
                />
            </Box>

            {/* Package List */}
            <div className="package-sizes">
                <Card>
                    <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <div>
                                <h5 style={{ fontSize: "20px", fontWeight: 600 }}>
                                    Package Size Categories
                                </h5>
                                <p style={{ fontSize: "14px", fontWeight: 400, marginBottom: 0 }}>
                                    Define Pricing For Different Package Sizes
                                </p>
                            </div>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                sx={{
                                    backgroundColor: "rgba(89, 50, 234, 1)",
                                    "&:hover": { backgroundColor: "rgba(89, 50, 234, 0.9)" },
                                }}
                                onClick={() => handleOpenDialog()}
                            >
                                Add Package Size
                            </Button>
                        </Box>

                        <Divider sx={{ borderBottom: "2px solid #f3f4f6", my: 2 }} />

                        {packages.map((pkg) => (
                            <Box key={pkg.id} mb={3}>
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <div className="d-flex">
                                        <Typography variant="subtitle1" fontWeight={600} fontSize={18}>
                                            {pkg.packageType}
                                        </Typography>
                                        <div
                                            style={{
                                                display: "inline-block",
                                                padding: "6px 12px",
                                                borderRadius: "15px",
                                                fontSize: "14px",
                                                fontWeight: 500,
                                                marginLeft: "10px",
                                                width: "80px",
                                                textAlign: "center",
                                                height: "30px",
                                                backgroundColor:
                                                    pkg.status === "active" ? "#E8F5E9" : "#FFEBEE",
                                                color: pkg.status === "active" ? "#2e7d32" : "#c62828",
                                            }}
                                        >
                                            {pkg.status.charAt(0).toUpperCase() + pkg.status.slice(1).toLowerCase()}
                                        </div>
                                    </div>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Typography variant="body2" sx={{ mr: 1 }}>
                                            Created At: {new Date(pkg.createdAt).toLocaleDateString("en-IN")}
                                        </Typography>
                                        <IconButton color="primary" onClick={() => handleOpenDialog(pkg)}>
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            color="error"
                                            onClick={() => {
                                                setDeleteId(pkg.id);
                                                setDeleteDialogOpen(true);
                                            }}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Box>
                                </Box>

                                <Typography variant="body2" sx={{ my: 1, color: "text.secondary" }}>
                                    {pkg.description}
                                </Typography>

                                <Box display="flex" gap={2} flexWrap="wrap">
                                    <DetailBox label="Min Weight" value={`${pkg.minWeight} kg`} />
                                    <DetailBox label="Max Weight" value={`${pkg.maxWeight} kg`} />
                                    <DetailBox label="Base Price" value={`₹${pkg.basePrice}`} />
                                    <DetailBox label="Per Mile Rate" value={`₹${pkg.perMileRate}`} />
                                </Box>

                                <Divider sx={{ borderBottom: "2px solid #f3f4f6", my: 2 }} />
                            </Box>
                        ))}
                    </CardContent>
                </Card>

                {/* Add/Edit Package Dialog */}
                <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
                    <DialogTitle>{editMode ? "Edit Package Size" : "Add Package Size"}</DialogTitle>
                    <DialogContent dividers>
                        <Box display="flex" flexDirection="column" gap={2} mt={1}>
                            <TextField
                                label="Package Type"
                                variant="outlined"
                                value={packageType}
                                onChange={(e) => setPackageType(e.target.value)}
                            />
                            <TextField
                                label="Min Weight"
                                type="number"
                                value={minWeight}
                                onChange={(e) => setMinWeight(e.target.value ? Number(e.target.value) : "")}
                            />
                            <TextField
                                label="Max Weight"
                                type="number"
                                value={maxWeight}
                                onChange={(e) => setMaxWeight(e.target.value ? Number(e.target.value) : "")}
                            />
                            <TextField
                                label="Base Price"
                                type="number"
                                value={basePrice}
                                onChange={(e) => setBasePrice(e.target.value ? Number(e.target.value) : "")}
                            />
                            <TextField
                                label="Per Mile Rate"
                                type="number"
                                value={perMileRate}
                                onChange={(e) => setPerMileRate(e.target.value ? Number(e.target.value) : "")}
                            />
                            <TextField
                                label="Handling Fee"
                                type="number"
                                value={handlingFee}
                                onChange={(e) => setHandlingFee(e.target.value ? Number(e.target.value) : "")}
                            />
                            <FormControl fullWidth>
                                <Select value={status} onChange={(e) => setStatus(e.target.value)} displayEmpty>
                                    <MenuItem value="" disabled>Select Status</MenuItem>
                                    <MenuItem value="active">Active</MenuItem>
                                    <MenuItem value="inactive">Inactive</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                label="Description"
                                fullWidth
                                multiline
                                rows={3}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog} color="secondary">Cancel</Button>
                        <Button variant="contained" onClick={handleSave}>
                            {editMode ? "Update" : "Save"}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Delete Confirmation */}
                <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                    <DialogTitle>Confirm Delete</DialogTitle>
                    <DialogContent>
                        Are you sure you want to delete this package?
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                        <Button color="error" variant="contained" onClick={handleDeleteConfirm}>Delete</Button>
                    </DialogActions>
                </Dialog>
            </div>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
}

const CardSummary = ({ title, value }) => (
    <Card sx={{ minWidth: 275, flex: "1 1 30%", bgcolor: "white", borderRadius: "15px", boxShadow: 2 }}>
        <CardContent>
            <Box display="flex" alignItems="center" gap={2}>
                <Box
                    sx={{
                        width: 50,
                        height: 50,
                        borderRadius: "8px",
                        backgroundColor: "#F3E5F5",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <CurrencyRupeeIcon fontSize="medium" htmlColor="#6a1b9a" />
                </Box>
                <Box>
                    <Typography sx={{ color: "text.secondary", fontSize: 14 }}>{title}</Typography>
                    <Typography variant="h6">{value}</Typography>
                </Box>
            </Box>
        </CardContent>
    </Card>
);

const DetailBox = ({ label, value }) => (
    <div style={{ background: "#f3f4f6", padding: "15px", borderRadius: "10px", minWidth: "200px", height: "70px" }}>
        <p style={{ fontSize: "14px", margin: 0 }}>{label}</p>
        <Typography fontSize="18px" marginBottom={0}>{value}</Typography>
    </div>
);

export default Package;
